import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
// @mui
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
// hooks
import useSettings from "../../hooks/useSettings";
import useResponsive from "../../hooks/useResponsive";
import useCollapseDrawer from "../../hooks/useCollapseDrawer";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from 'react-i18next';
// config
import { HEADER, NAVBAR } from "../../config";
//
import DashboardHeader from "./header";
import NavbarVertical from "./navbar/NavbarVertical";
import NavbarHorizontal from "./navbar/NavbarHorizontal";

// ----------------------------------------------------------------------

const MainStyle = styled("main", {
  shouldForwardProp: (prop) => prop !== "collapseClick",
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: 20,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up("sm")]: {
    paddingLeft: 65,
    paddingRight: 16,
    paddingTop: 20,
    paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create("margin-left", {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { collapseClick, isCollapse } = useCollapseDrawer();

  const { role, country, isWebVersion } = useAuth();

  const { i18n, t } = useTranslation();

  const { themeLayout } = useSettings();

  const isDesktop = useResponsive("up", "sm");

  const [open, setOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(true);

  const verticalLayout = themeLayout === "vertical";

  /**
   * Select user lang based on user's country when it's not web version
   */
  useEffect(() => {
    console.log('[i18n v1.2] country:', country, '| isWebVersion:', isWebVersion(), '| role:', role);
    if ( country && !isWebVersion() && role !== 'ADMIN' ) {
      const countryToLang = {
        // Italiano
        'Italy': 'it-IT', 'San Marino': 'it-IT', 'Vatican City': 'it-IT',
        // Tedesco
        'Germany': 'de-DE', 'Austria': 'de-DE', 'Switzerland': 'de-DE', 'Liechtenstein': 'de-DE', 'Luxembourg': 'de-DE',
        // Francese
        'France': 'fr-FR', 'Belgium': 'en-US', 'Monaco': 'fr-FR', 'Haiti': 'fr-FR',
        'Senegal': 'fr-FR', 'Mali': 'fr-FR', 'Niger': 'fr-FR', 'Burkina Faso': 'fr-FR', "Cote d'Ivoire": 'fr-FR',
        'Guinea': 'fr-FR', 'Benin': 'fr-FR', 'Togo': 'fr-FR', 'Cameroon': 'fr-FR', 'Chad': 'fr-FR',
        'Central African Republic': 'fr-FR', 'Congo': 'fr-FR', 'Congo, Democratic Republic of the': 'fr-FR',
        'Gabon': 'fr-FR', 'Comoros': 'fr-FR', 'Djibouti': 'fr-FR', 'Madagascar': 'fr-FR', 'Rwanda': 'fr-FR',
        'Burundi': 'fr-FR', 'Mauritius': 'fr-FR', 'Mayotte': 'fr-FR',
        'French Guiana': 'fr-FR', 'French Polynesia': 'fr-FR', 'Guadeloupe': 'fr-FR', 'Martinique': 'fr-FR',
        'New Caledonia': 'fr-FR', 'Saint Pierre and Miquelon': 'fr-FR', 'Wallis and Futuna': 'fr-FR',
        // Spagnolo
        'Spain': 'es-ES', 'Argentina': 'es-ES', 'Mexico': 'es-ES', 'Colombia': 'es-ES', 'Chile': 'es-ES',
        'Peru': 'es-ES', 'Venezuela': 'es-ES', 'Ecuador': 'es-ES', 'Bolivia': 'es-ES', 'Paraguay': 'es-ES',
        'Uruguay': 'es-ES', 'Costa Rica': 'es-ES', 'Panama': 'es-ES', 'Honduras': 'es-ES', 'El Salvador': 'es-ES',
        'Nicaragua': 'es-ES', 'Guatemala': 'es-ES', 'Cuba': 'es-ES', 'Dominican Republic': 'es-ES', 'Puerto Rico': 'es-ES',
        'Equatorial Guinea': 'es-ES',
      };
      const lang = countryToLang[country] || 'en-US';
      i18n.changeLanguage(lang);
    }
  }, []);

  const handleToggleNav = () => {
    if ( navOpen ) {
      setNavOpen(false);
    } else {
      setNavOpen(true);
    }
  }

  if (verticalLayout) {
    return (
      <>
        <DashboardHeader
          onOpenSidebar={() => setOpen(true)}
          verticalLayout={verticalLayout}
        />

        {isDesktop ? (
          <NavbarHorizontal />
        ) : (
          <NavbarVertical
            isOpenSidebar={open}
            onCloseSidebar={() => setOpen(false)}
          />
        )}

        <Box
          component="main"
          sx={{
            px: { lg: 2 },
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: { sm: "flex" },
        minHeight: { sm: 1 },
      }}
    >
      {/* <DashboardHeader
        isCollapse={isCollapse}
        onOpenSidebar={() => setOpen(true)}
      /> */}

      <NavbarVertical
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
        isOpen={navOpen}
        handleToggle={handleToggleNav}
      />

      <MainStyle collapseClick={collapseClick} sx={{ paddingLeft: navOpen ? "310px !important" : "65px !important" }}>
        <Outlet />
      </MainStyle>
    </Box>
  );
}
